import { UserTableToolbar } from '@/contexts/backoffice/users/ui/components/table/UserTableToolbar'
import { CloseRounded, Delete, RotateLeftRounded } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fade, Grid, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams, GridRowSelectionModel, GridRowsProp } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { deleteUser, restoreUser, showUser } from '../../../../../redux/actions/users'
import { IRootState } from '../../../../../redux/types'
import CellTitleValue from '../../../../shared/components/CellTitleValue'
import CustomChip from '../../../../shared/components/CustomChip'
import { CancelButton, DeleteButton, SaveButton } from '../../../../shared/components/inputs'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { UserListApi } from '../../models/user'
import { isCommercial } from '../../utils/RolesHelper'
import CustomVerifiedIcon from './CustomVerifiedIcon'

function UserTable({ userIds, setUserIds, filters }: any) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()
  const isMobile = useScreenWidth(700)

  const { users } = useSelector((state: IRootState) => state.user)

  const [openDeleteUser, setOpenDeleteUser] = useState(false)
  const [userSelected, setUserSelected] = useState<any>(false)

  const initialState = {
    columns: {
      columnVisibilityModel: {
        id: !isMobile,
        verification: filters.role_id === 6,
        code: isCommercial(filters.role_id),
        province: !isMobile,
        postalcode: !isMobile,
        phone: !isMobile,
        email: !isMobile
      }
    },
    pagination: {
      paginationModel: { pageSize: 10 }
    }
  }
  const rows: GridRowsProp<UserListApi> = users ?? []
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80, renderCell: (params: any) => `PL-${params.id}` },
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Box display="flex" alignItems="center" gap={2}>
            {(() => {
              switch (params.row.status) {
                case 0:
                  return <CustomChip size="small" color="secondary" />
                case 1:
                  return <CustomChip size="small" color="primary" />
                default:
                  return <CustomChip size="small" color="secondary" />
              }
            })()}
            <Tooltip placement="bottom-start" TransitionComponent={Fade} TransitionProps={{ timeout: 400 }} title={params.value}>
              <CellTitleValue value={params.value} />
            </Tooltip>
          </Box>
        )
      }
    },
    {
      headerName: t('email'),
      field: 'email',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip placement="bottom-start" TransitionComponent={Fade} TransitionProps={{ timeout: 400 }} title={params.value}>
              <Typography
              // sx{{
              //   whiteSpace: 'nowrap',
              //   textOverflow: 'ellipsis',
              //   overflow: 'hidden',
              //   maxWidth: 200,
              //   fontWeight: 300
              // }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          </Box>
        )
      }
    },
    { headerName: t('phone'), field: 'phone' },
    {
      headerName: t('admin.people.verification'),
      field: 'verification',
      renderCell: (params: any) => <CustomVerifiedIcon value={params.value} />
    },
    { headerName: t('code'), field: 'code' },
    { headerName: t('province'), field: 'province', width: 120 },
    {
      field: 'actions',
      type: 'actions',
      width: 30,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={params.id}
          icon={<ViewIcon />}
          label={t('view')}
          onClick={() => {
            window.open(`/admin/usuarios/${params.id}`, '_blank')
          }}
          showInMenu
        />,
        <GridActionsCellItem
          id="edit-user"
          key={params.id}
          icon={<EditIcon />}
          label={t('edit')}
          onClick={() => {
            dispatch(showUser(params.id))
          }}
          showInMenu
        />,
        <GridActionsCellItem
          key={params.id}
          icon={<DeleteIcon />}
          label={t('delete')}
          onClick={() => {
            setOpenDeleteUser(!openDeleteUser)
            setUserSelected(params.row)
          }}
          showInMenu
        />
      ]
    }
  ]

  return (
    <>
      <div style={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnMenu
          checkboxSelection
          density="comfortable"
          autoHeight
          pageSizeOptions={[10, 20, 30]}
          onRowSelectionModelChange={(newSelectionModel: GridRowSelectionModel) => {
            setUserIds(newSelectionModel as number[])
          }}
          rowSelectionModel={userIds}
          disableRowSelectionOnClick
          initialState={initialState}
          disableColumnFilter
          disableDensitySelector
          slots={{ toolbar: UserTableToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true
            }
          }}
          sx={{
            '& .MuiDataGrid-panelWrapper': {
              position: 'relative',
              top: '100%',
              right: 'auto !important',
              zIndex: 1
            }
          }}
        />
      </div>

      <Dialog
        open={openDeleteUser}
        fullWidth={true}
        fullScreen={isMobile}
        maxWidth={!isMobile && 'sm'}
        onClose={() => setOpenDeleteUser(!openDeleteUser)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">
              {userSelected.deleted_at !== null ? t('restore') : t('delete')} {t('user')}
            </Typography>
            <Button onClick={() => setOpenDeleteUser(!openDeleteUser)} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>
        <DialogContent sx={theme => ({ paddingBottom: theme.spacing(3), paddingTop: theme.spacing(2) })}>
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item xs={12}>
              <Typography>
                {userSelected.deleted_at != null ? t('admin.people.restore-user-text') : t('admin.people.delete-user-text')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setOpenDeleteUser(!openDeleteUser)}>{t('cancel')}</CancelButton>
          {userSelected.deleted_at != null ? (
            <SaveButton
              autoFocus
              id="btn-handlelRestore"
              endIcon={<RotateLeftRounded />}
              onClick={() => {
                dispatch(restoreUser(userSelected.id))
                setOpenDeleteUser(!openDeleteUser)
              }}
            >
              {t('restore')}
            </SaveButton>
          ) : (
            <DeleteButton
              autoFocus
              id="btn-handleldelete"
              endIcon={<Delete />}
              onClick={() => {
                dispatch(deleteUser(userSelected.id))
                setOpenDeleteUser(!openDeleteUser)
              }}
            >
              {t('delete')}
            </DeleteButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserTable
